import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, InputCheckBox as CheckBox, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import createComputed from 'modules/core/hooks/createComputed'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import bookkeepers from 'modules/bookkeepers/store/collections/bookkeepers'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import { getStatusStepTitle } from 'modules/payroll_bundle/components/PolicyPeriodDashboardContent/RegularCycleSummary/copies'
import { PayrollStatus } from 'modules/payroll_bundle/lib/enums'
import pathForPolicyPeriodStatus from 'modules/payroll_bundle/lib/pathForPolicyPeriodStatus'
import { usePayrollContext } from 'modules/payroll_bundle/lib/payrollContext'
import PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'
import DatevApiSupplementsSyncPreview from 'modules/payroll_integrations_datev_api/components/SupplementsSyncPreview'

import Description from './DescriptionBox'

interface Props {
  onClose?: () => void
  policyPeriod: PolicyPeriod
}

const useStatusDescription = createComputed((status: PayrollStatus) => {
  const statusDescriptions = PolicyPeriod.StatusDescriptions()

  return statusDescriptions[status]
})

const MoveFromPayrollCalculationText = () => (
  <Text.Normal size="200" weight="medium">
    {i18n.t('payroll_bundle.policy_period.status.modal_description.moved_from_payroll_calculation')}
  </Text.Normal>
)

const StatusModal = ({ policyPeriod, onClose }: Props) => {
  const { selectedStatus: status, payrollDispatch } = usePayrollContext()

  const policyId: string = policyPeriod.attributes.policy_id

  const onModalClose = () => {
    payrollDispatch({
      type: 'HIDE_CHANGE_STATUS_MODAL',
    })
  }

  const selectedStatusLabel = getStatusStepTitle(status)

  const [disabled, setDisabled] = React.useState(false)
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [checked, setChecked] = React.useState(false)
  const [employeeSelection, setEmployeeSelection] =
    React.useState<EmployeeSelectorV2MultiSelectState>({
      type: 'manual',
      ids: [],
    })
  const statusDescription = useStatusDescription(status).get()
  const notifyForBookkeeper =
    PayrollStatus.PAYROLL_CALCULATION === status || policyPeriod.isPayrollCalculation
  const initialStatus = policyPeriod.attributes.status

  const policyPeriodId = policyPeriod.id

  const resultPath = useUnsafeMemo(() => {
    return pathForPolicyPeriodStatus({ status, policyId, policyPeriodId, navigation })
  }, [policyId, policyPeriodId, status, navigation])

  async function onSubmit() {
    setDisabled(true)

    const data = {
      status,
      notify_employee: checked || notifyForBookkeeper,
      employee_ids: [], // Keep empty for backward compatibility
      employee_selection: employeeSelection,
    }

    await asyncAction(() => policyPeriod.changeStatus(data)).finally(() => {
      if (onClose) onClose()
      else onModalClose()
    })
    track(trackingEvents.payroll.changePayrollStatus, {
      period_status: selectedStatusLabel,
      from_status: initialStatus,
      to_staus: status,
      number_of_employees:
        checked || notifyForBookkeeper
          ? employeeSelection.type === 'manual'
            ? employeeSelection.ids.length
            : 0
          : 0,
    })

    setDisabled(false)
    !!resultPath && navigate(resultPath)
  }

  useAsyncData(() => bookkeepers.fetch(), [])

  const disbledChangeStatusButton = policyPeriod.attributes.status === status || disabled
  const showNotifyCollaborator = !!statusDescription.notification
  const showEmployeeSelector = checked && showNotifyCollaborator

  const showMovedFromCalculationDescription =
    policyPeriod.isPayrollCalculation && status !== PayrollStatus.PAYROLL_CALCULATION

  const changeToPayrollCalculation =
    !policyPeriod.isPayrollCalculation && status == PayrollStatus.PAYROLL_CALCULATION

  const statusDescriptions = PolicyPeriod.StatusDescriptions()

  return (
    <Modal
      title={i18n.t('payroll_bundle.policy_period.status.modal.heading', {
        status: selectedStatusLabel,
      })}
      description={statusDescription.main_description}
      illustration="top-up"
      onClose={onClose ?? onModalClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button onClick={onClose ?? onModalClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button type="primary" disabled={disbledChangeStatusButton} submit>
            {i18n.t('common.confirm')}
          </Button>
        </>
      }
    >
      <Box>
        <Box gap="s16">
          {!!statusDescription.additional_modal_description && (
            <Text.Normal size="200" weight="medium">
              {statusDescription.additional_modal_description}
            </Text.Normal>
          )}
          {showMovedFromCalculationDescription && !showNotifyCollaborator && (
            <MoveFromPayrollCalculationText />
          )}

          {changeToPayrollCalculation && (
            <>
              <DatevApiSupplementsSyncPreview
                policyPeriodId={policyPeriodId}
                disableSubmitCallback={setDisabled}
              />
            </>
          )}

          {showNotifyCollaborator && (
            <>
              <Box minWidth="s24" flexDirection="row" gap="s8" alignItems="center">
                <CheckBox onClick={() => setChecked(!checked)} checked={checked} />
                <Text size="200" weight="semibold" color="grey800">
                  {statusDescriptions[status]?.notification}
                </Text>
              </Box>
              {showEmployeeSelector && (
                <EmployeeSelectorV2 multiple onChange={setEmployeeSelection} />
              )}
              {showMovedFromCalculationDescription && (
                <Description>
                  <MoveFromPayrollCalculationText />
                </Description>
              )}
            </>
          )}
        </Box>
      </Box>
    </Modal>
  )
}

export default observer(StatusModal)
